import { enUS, es, de, fr, it, ja } from 'date-fns/locale';
import type { Locale } from 'date-fns';

const localeCache = new Map<string, Locale>();

const createCapitalizedLocale = (baseLocale: Locale, key: string): Locale => {
  if (localeCache.has(key)) {
    return localeCache.get(key)!;
  }

  if (!baseLocale.localize) {
    localeCache.set(key, baseLocale);
    return baseLocale;
  }

  const capitalizedLocale: Locale = {
    ...baseLocale,
    localize: {
      ...baseLocale.localize,
      month: (n: number) => {
        const month = baseLocale.localize?.month(n) || '';
        return month.charAt(0).toUpperCase() + month.slice(1);
      },
      day: (n: number) => {
        const day = baseLocale.localize?.day(n) || '';
        if (baseLocale.code === 'en-US' || baseLocale.code === 'en') {
          const dayAbbreviations: Record<number, string> = {
            0: 'Sun',
            1: 'Mon',
            2: 'Tue',
            3: 'Wed',
            4: 'Thu',
            5: 'Fri',
            6: 'Sat'
          };
          return dayAbbreviations[n] || day;
        }
        const dayAbbr = day.substring(0, 3);
        return dayAbbr.charAt(0).toUpperCase() + dayAbbr.slice(1);
      },
      ordinalNumber: baseLocale.localize.ordinalNumber,
      era: baseLocale.localize.era,
      quarter: baseLocale.localize.quarter,
      dayPeriod: baseLocale.localize.dayPeriod
    }
  };

  localeCache.set(key, capitalizedLocale);
  return capitalizedLocale;
};

const localeMap: Record<string, Locale> = {
  en: createCapitalizedLocale(enUS, 'en'),
  'en-US': createCapitalizedLocale(enUS, 'en-US'),
  'en-GB': createCapitalizedLocale(enUS, 'en-GB'),
  'en-AU': createCapitalizedLocale(enUS, 'en-AU'),
  es: createCapitalizedLocale(es, 'es'),
  de: createCapitalizedLocale(de, 'de'),
  fr: createCapitalizedLocale(fr, 'fr'),
  it: createCapitalizedLocale(it, 'it'),
  ja: createCapitalizedLocale(ja, 'ja')
};

/**
 * Get the appropriate date-fns locale for the given language code
 * Falls back to enUS if the locale is not supported
 * Ensures proper capitalization of month names and abbreviated day names
 */
export const getDateFnsLocale = (language: string): Locale => {
  return localeMap[language] || createCapitalizedLocale(enUS, language);
};
